import React from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { Collapse, Alert } from 'antd';

const { Panel } = Collapse;

const TslsMultiChart = (props) => {
    const result = props.result || {};

    if (!result || result.error || result.message) {
        return (
            <Collapse bordered={false} defaultActiveKey={[1]}>
                <Panel key={1} header="Two Stage Least Squares" style={{ color: 'red' }}>
                    <Alert
                        message="Error"
                        description={result?.message || result?.error || 'TSLS error'}
                        type="error"
                    />
                </Panel>
            </Collapse>
        );
    }

    const xName = result.x_display_name || 'X';
    const yName = result.y_display_name || 'Y';
    const scatterXTitle =
        Array.isArray(result.exogenous_names) && result.exogenous_names.length > 0
            ? `${xName} (endogenous)`
            : xName;

    /* ------------------------------------------------------------------ */
    /* Main scatter chart: X vs Y                                          */
    /* ------------------------------------------------------------------ */
    const scatterPoints = (result.data || [])
        .map((d) => [
            typeof d.x === 'number' ? d.x : null,
            typeof d.y === 'number' ? d.y : null,
        ])
        .filter(([x, y]) => x !== null && y !== null);

    const scatterOptions = {
        chart: {
            marginRight: 30,
            marginLeft: 77,
            marginTop: 20,
            renderTo: 'chart-container',
            zoomType: 'xy',
            height: 400,
        },
        boost: { useGPUTranslations: true, usePreAllocated: true },
        credits: { enabled: false },
        title: { text: undefined, style: { fontSize: '11px' } },
        subtitle: { text: undefined, style: { fontSize: '10px' } },
        xAxis: [{
            endOnTick: true,
            tickLength: 0,
            lineWidth: 0,
            labels: { enabled: true, style: { fontWeight: 'normal', fontSize: '11px' } },
            title: { text: scatterXTitle, style: { fontWeight: 'normal', fontSize: '14px' } },
        }],
        yAxis: [{
            labels: { style: { fontSize: '11px' } },
            title: { text: yName, style: { fontWeight: 'normal', fontSize: '14px' } },
        }],
        navigation: {
            buttonOptions: {
                align: 'right', backgroundColor: '#ffffff', borderColor: '#B0B0B0',
                borderRadius: 3, borderWidth: 1, enabled: true, height: 20,
                symbolFill: '#E0E0E0', symbolSize: 12, symbolStroke: '#A0A0A0',
                symbolStrokeWidth: 1, verticalAlign: 'top', width: 24, y: 10,
            },
            menuStyle: { border: '1px solid #A0A0A0', background: '#FFFFFF' },
            menuItemStyle: { padding: '0 5px', background: 'NONE', color: '#303030' },
            menuItemHoverStyle: { background: '#4572A5', color: '#FFFFFF' },
        },
        tooltip: {
            useHTML: true,
            shape: 'square',
            shared: false,
            borderRadius: 0,
            borderWidth: 0,
            shadow: false,
            enabled: true,
            backgroundColor: 'none',
            formatter: function () {
                return `<div class="tooltipContainer">${this.x.toFixed(4)}:${this.y.toFixed(4)}</div>`;
            },
        },
        plotOptions: { scatter: { marker: { radius: 2 } } },
        legend: { enabled: false },
        series: [{
            name: 'Observations',
            color: '#407CB1',
            type: 'scatter',
            yAxis: 0,
            data: scatterPoints,
            turboThreshold: 50000,
        }],
    };

    /* ------------------------------------------------------------------ */
    /* Residual scatter chart: fitted vs standardised residuals + LOWESS   */
    /* ------------------------------------------------------------------ */
    const residPoints = (result.chart_resid?.resid || [])
        .map(([x, y]) => [
            typeof x === 'number' ? x : null,
            typeof y === 'number' ? y : null,
        ])
        .filter(([x, y]) => x !== null && y !== null);

    const fitPoints = (result.chart_resid?.fit || [])
        .map(([x, y]) => [
            typeof x === 'number' ? x : null,
            typeof y === 'number' ? y : null,
        ])
        .filter(([x, y]) => x !== null && y !== null);

    const residOptions = {
        chart: {
            marginRight: 30,
            marginLeft: 77,
            marginTop: 20,
            renderTo: 'chart-container-resid',
            zoomType: 'xy',
            height: 400,
        },
        boost: { useGPUTranslations: true, usePreAllocated: true },
        credits: { enabled: false },
        title: { text: undefined, style: { fontSize: '11px' } },
        subtitle: { text: undefined, style: { fontSize: '10px' } },
        xAxis: [{
            endOnTick: true,
            tickLength: 0,
            lineWidth: 0,
            labels: { enabled: true, style: { fontWeight: 'normal', fontSize: '11px' } },
            title: {
                text: result.chart_resid?.x_name || 'Fitted values',
                style: { fontWeight: 'normal', fontSize: '14px' },
            },
        }],
        yAxis: [{
            labels: { style: { fontSize: '11px' } },
            title: {
                text: result.chart_resid?.y_name || 'Standardized residuals',
                style: { fontWeight: 'normal', fontSize: '14px' },
            },
        }],
        navigation: {
            buttonOptions: {
                align: 'right', backgroundColor: '#ffffff', borderColor: '#B0B0B0',
                borderRadius: 3, borderWidth: 1, enabled: true, height: 20,
                symbolFill: '#E0E0E0', symbolSize: 12, symbolStroke: '#A0A0A0',
                symbolStrokeWidth: 1, verticalAlign: 'top', width: 24, y: 10,
            },
            menuStyle: { border: '1px solid #A0A0A0', background: '#FFFFFF' },
            menuItemStyle: { padding: '0 5px', background: 'NONE', color: '#303030' },
            menuItemHoverStyle: { background: '#4572A5', color: '#FFFFFF' },
        },
        tooltip: {
            useHTML: true,
            shape: 'square',
            shared: false,
            borderRadius: 0,
            borderWidth: 0,
            shadow: false,
            enabled: true,
            backgroundColor: 'none',
            formatter: function () {
                return `<div class="tooltipContainer">${this.x.toFixed(4)}:${this.y.toFixed(4)}</div>`;
            },
        },
        plotOptions: { scatter: { marker: { radius: 2 } } },
        legend: { enabled: false },
        series: [
            {
                name: 'Residuals',
                color: '#407CB1',
                type: 'scatter',
                yAxis: 0,
                data: residPoints,
                turboThreshold: 50000,
            },
            {
                name: 'LOWESS',
                type: 'spline',
                color: '#60091D',
                yAxis: 0,
                data: fitPoints,
                marker: { enabled: false },
                turboThreshold: 50000,
            },
        ],
    };

    /* ------------------------------------------------------------------ */
    /* Statistics table                                                     */
    /* ------------------------------------------------------------------ */
    const statRows = (result.stat || []).map((row, i) => (
        <tr key={i}>
            <td className="title">{row.display_name}</td>
            <td className="value">{String(row.value)}</td>
        </tr>
    ));

    const statTable = (
        <div className="statistics-row-box">
            <div className="table-box-fullproject-view">
                <h3 style={{ marginTop: '10px' }}>Statistics</h3>
                <table className="table-table">
                    <tbody>{statRows}</tbody>
                </table>
            </div>
        </div>
    );

    /* ------------------------------------------------------------------ */
    /* Coefficient table builder                                            */
    /* ------------------------------------------------------------------ */
    const buildCoeffTable = (rows, title) => {
        const headers = ['', 'Coeff', 'Std Error', 't-Stat'];
        const headerCells = headers.map((h) => <th key={h}>{h}</th>);
        const dataRows = (rows || []).map((dr, i) => (
            <tr key={i}>
                <td>{dr.display_name}</td>
                <td>{dr.coeff}</td>
                <td>{dr.std_err}</td>
                <td>{dr.T_Stat}</td>
            </tr>
        ));
        return (
            <div className="table-box-fullproject">
                <h3>{title}</h3>
                <table className="table-regression">
                    <thead><tr>{headerCells}</tr></thead>
                    <tbody>{dataRows}</tbody>
                </table>
            </div>
        );
    };

    /* ------------------------------------------------------------------ */
    /* Residual chart section header                                        */
    /* ------------------------------------------------------------------ */
    const residualChartBlock = (
        <div className="table-box-fullproject">
            <h3>Standardized Residuals vs Fitted</h3>
            <HighchartsReact highcharts={Highcharts} options={residOptions} />
        </div>
    );

    const fullStats = (
        <div>
            {statTable}
            {buildCoeffTable(result.coeff, 'Ordinary Std Error')}
            {buildCoeffTable(result.newey_west, 'Newey-West Std Error')}
            {residualChartBlock}
        </div>
    );

    return (
        <Collapse bordered={false} defaultActiveKey={[1]}>
            <Panel key={1} header="Two Stage Least Squares">
                <HighchartsReact highcharts={Highcharts} options={scatterOptions} />
                {fullStats}
            </Panel>
        </Collapse>
    );
};

export default React.memo(TslsMultiChart);
